import { Building } from "../../../building/Building";
import { LeanTo } from "./LeanTo";
import { LeanToWraparoundSetting } from "../../../config/LeanToWraparoundSetting";
import { RoofAddon } from "../../addon/RoofAddon";
import { Object3D, Mesh, Vector2 } from "three";
import { BuildingGeometry } from "../../../building/BuildingGeometry";
import { MaterialManager } from "../../../material/MaterialManager";
import { THREEUtils } from "../../../utils/THREEUtils";
import { SignalConfig } from "../../../config/SignalConfig";

export interface ILeanToWraparoundConfig
{
	setting: LeanToWraparoundSetting;
	building: Building;
	leanTo: LeanTo;
	bottom: boolean;
	container: Object3D;
}

/**
 * Represents a triangular part (half) of a wraparound corner.
 * A wraparound corner consists of 2 triangular parts, each belongs to a LeanTo.
 * A LeanTo can have 0/1/2 triangular parts.
 */
export class LeanToWraparound
{
	private _config: ILeanToWraparoundConfig;
	private _setting: LeanToWraparoundSetting;

	private _container: Object3D;

	private _roof: Mesh;
	private _bottomRoof: Mesh; // the bottom part should be always white

	private _trim: RoofAddon;

	constructor(config:ILeanToWraparoundConfig)
	{
		this._config = config;
		this._setting = config.setting;
		this.init();
	}

	private init()
	{
		this.createMesh();
		this._setting.enabled.signal.add(this.update);
		this._setting.getOtherLeanTo(this._config.leanTo.config).length.signal.add(this.update);
	}

	private createMesh()
	{
		this._container = new Mesh();

		var materials = this._config.building.materials;
		var roofGeometry = BuildingGeometry.createEndRoofGeometry(false, this._config.bottom, !this._config.bottom);

		this._roof = new Mesh(roofGeometry, materials.roof_singleSided);
		this._roof.rotation.x = -Math.PI/2;
		this._roof.rotation.z = Math.PI * 3/2;
		this._roof.castShadow = true;

		this._container.add(this._roof);


		var bottomRoof_geometry = BuildingGeometry.createEndRoofGeometry(false, this._config.bottom, !this._config.bottom);
		this._bottomRoof = new Mesh(bottomRoof_geometry, materials.interior);
		this._bottomRoof.rotation.x = -Math.PI/2;
		this._bottomRoof.rotation.z = Math.PI * 3/2;

		this._container.add(this._bottomRoof);


		if (!this._config.bottom)
		{
			// by convention we add the trim on the top wraparound (could be the bottom)
			this.createTrim();
		}
	}

	private createTrim()
	{
		var self = this;

		// aka ridge roll on wraparounds

		var trimConfig = {
			"id": "",
			"label": "",
			"mesh": {
				"1": "#0200 - Ridge Roll - 1/12 Pitch",
				"2": "#0200 - Ridge Roll - 2/12 Pitch",
				"3": "#0200 - Ridge Roll - 3/12 Pitch",
				"4": "#0200 - Ridge Roll - 4/12 Pitch",
				"5": "#0200 - Ridge Roll - 4/12 Pitch"
			},
			"type": "roof",
			"size": {
				"length": 0
			},
			"material": MaterialManager.getInstance().trim_roofColor,
			"pitch": new SignalConfig({
				value: 1
			}) //this._building.config.building.pitch
		};

		this._trim = new RoofAddon(trimConfig);
		this._trim.addToScene(this._container); //Main.getInstance().scene.building.scene3D);

		this._trim.show();

		this._trim.updatePitch(true, 1);
		this._trim.container.rotation.z = 0;
	}

	// this will be called initially by LeanTo
	public update = () =>
	{
		var leanTo = this._config.leanTo;
		var metrics = leanTo.metrics;
		var enabled = this._setting.enabled.value;
		this.setShown(enabled);

		// actually this is not needed at the moment, because lengths has to match
		var otherLeanTo = this._setting.getOtherLeanTo(leanTo.config);

		//this._roof.scale.y = metrics.length / Math.cos(metrics.alpha);
		//this._roof.scale.x = otherLeanTo.length.value * 2;
		//THREEUtils.scaleUVs(this._roof.geometry, new Vector2(this._roof.scale.x, this._roof.scale.y));
		//
		//this._roof.rotation.y = -metrics.alpha;

		var scaleY = metrics.length / Math.cos(metrics.alpha);
		var scaleX = otherLeanTo.length.value * 2;
		var rotationY = -metrics.alpha;

		this.updateRoof(this._roof,       scaleX, scaleY, rotationY);
		this.updateRoof(this._bottomRoof, scaleX, scaleY, rotationY);

		this._container.position.x = -metrics.length;
		this._container.position.z = -metrics.building_length_half;
		this._container.position.y = metrics.wall_top;

		if (this._config.bottom)
		{
			this._container.position.z *= -1;
		}

		if (this._trim)
		{
			if (enabled)
			{
				this._trim.show();

				var trimContainer = this._trim.container; //['_mesh'];

				var mesh = this._trim.mesh;

				// TODO
				// quite a few ugly hacks here
				// reason: numerical errors, rotation/scale mess from blender files?

				// hack
				trimContainer.rotation.y = Math.PI / 4 - metrics.pitch * 0.005;

				// hack
				trimContainer.rotation.z = metrics.alpha; // 0; // RoofAddon changes this so we need to override

				trimContainer.rotation.x = -metrics.alpha; // elevation(pitch)

				trimContainer.position.x = metrics.length / 2;
				trimContainer.position.z = -metrics.length / 2;
				trimContainer.position.y = metrics.length * metrics.tan / 2 + 0.1;


				var trimLength = Math.sqrt(metrics.length * metrics.length + metrics.length * metrics.length);
				//this._trim.setLength(trimLength/10); // trim length needs to be divided by 10 to compensate with original length

				if (mesh)
				{

					mesh.scale.z = trimLength * mesh.scale.x;
					//mesh.position.z = mesh.scale.y / mesh.scale.x / 2;

					//console.log(this._trim, mesh, trimLength);
					//mesh.position.z = -trimLength;
				}
			}
			else
			{
				this._trim.hide();
			}
		}
	};

	private updateRoof(roof: Mesh, scaleX: number, scaleY: number, rotationY: number)
	{
		roof.scale.y = scaleY;
		roof.scale.x = scaleX;
		THREEUtils.scaleUVs(roof.geometry, new Vector2(roof.scale.x, roof.scale.y));

		roof.rotation.y = rotationY;
	}

	private setShown(value:boolean)
	{
		if (value)
		{
			this.show();
		}
		else
		{
			this.hide();
		}
	}

	private show()
	{
		this._roof.visible = true;
		this._bottomRoof.visible = true;
	}

	private hide()
	{
		this._roof.visible = false;
		this._bottomRoof.visible = false;
	}

	public get container()
	{
		return this._container;
	}
}
